/**
 * AdminMembersListPage - 회원 목록 페이지
 *
 * /centers/:centerId/members 경로로 접근하며,
 * Firestore에 저장된 회원 목록을 조회하고, 각 회원의 재등록 시기를 확인할 수 있다.
 * Studiomate 연동 시 "동기화하기" 버튼으로 회원 데이터를 동기화할 수 있다.
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useCurrentUser } from "@/components/AuthProvider";
import { useCenterContext } from "@/contexts/CenterContext";
import { CreateMemberModal } from "@/components/CreateMemberModal";
import {
  getAdminMembersList,
  getMembersSyncedAt,
  type Member,
} from "@/services/firestore/admin-members";
import { useSyncStudiomateMembers } from "@/hooks/useStudiomateIntegration";

export function AdminMembersListPage() {
  const { centerId } = useParams<{ centerId: string }>();
  const user = useCurrentUser();
  const { currentCenter } = useCenterContext();

  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);

  const syncMutation = useSyncStudiomateMembers();

  const fetchMembers = useCallback(async () => {
    if (!centerId) return;

    setIsLoading(true);
    setError(null);

    try {
      const [result, syncTime] = await Promise.all([
        getAdminMembersList(user.uid, Number(centerId)),
        getMembersSyncedAt(user.uid, Number(centerId)),
      ]);

      setMembers(result);
      setSyncedAt(syncTime);
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error("회원 목록 조회 실패"));
    } finally {
      setIsLoading(false);
    }
  }, [user.uid, centerId]);

  useEffect(() => {
    let cancelled = false;

    void fetchMembers().then(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [fetchMembers]);

  const handleSync = async () => {
    if (!centerId) return;

    setSyncSuccess(false);
    setCreateSuccess(false);
    try {
      const result = await syncMutation.mutateAsync({
        centerId: Number(centerId),
      });

      if (result.error) {
        setError(new Error(result.error.message));
        return;
      }

      setSyncedAt(result.membersSyncedAt);
      setSyncSuccess(true);
      // 동기화 후 목록 새로고침
      await fetchMembers();
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error("회원 동기화 실패"));
    }
  };

  const filteredMembers = useMemo(() => {
    const sorted = [...members].sort((a, b) => a.name.localeCompare(b.name));
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.trim().toLowerCase();
    return sorted.filter((m) => m.name.toLowerCase().includes(q));
  }, [members, searchQuery]);

  // --- 로딩 상태 ---
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 상세
          </Link>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  // --- 에러 상태 ---
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 센터 상세
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-700">오류 발생</h2>
          <p className="text-red-600 mt-1">{error.message}</p>
          <button
            onClick={() => {
              setError(null);
              void fetchMembers();
            }}
            className="mt-3 px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 뒤로가기 */}
      <div>
        <Link to={`/centers/${centerId}`} className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; 센터 상세
        </Link>
      </div>

      {/* 헤더 + 동기화 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">회원 목록 ({members.length}명)</h1>
          <p className="text-sm text-gray-500 mt-1">
            마지막 동기화:{" "}
            {syncedAt ? new Date(syncedAt).toLocaleString("ko-KR") : "동기화한 적 없음"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setSyncSuccess(false);
              setCreateSuccess(false);
              setIsCreateOpen(true);
            }}
            disabled={!currentCenter}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            회원 등록
          </button>
          <button
            onClick={() => void handleSync()}
            disabled={syncMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncMutation.isPending ? "동기화 중..." : "동기화하기"}
          </button>
        </div>
      </div>

      {/* 등록 성공 메시지 */}
      {createSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <p className="text-sm text-green-700">회원이 등록되었습니다.</p>
        </div>
      )}

      {/* 동기화 성공 메시지 */}
      {syncSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <p className="text-sm text-green-700">회원 목록이 동기화되었습니다.</p>
        </div>
      )}

      {/* 검색 필터 */}
      <div>
        <input
          type="text"
          placeholder="이름 검색"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-sm px-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* 빈 목록 */}
      {members.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-700">
            등록된 회원이 없습니다. "회원 등록" 또는 "동기화하기" 버튼을 사용하세요.
          </p>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
          <p className="text-gray-500">검색 결과가 없습니다.</p>
        </div>
      ) : (
        /* 회원 테이블 */
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  이름
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  연락처
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMembers.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <Link
                      to={`/centers/${centerId}/members/${member.id}`}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {member.name} &rarr;
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {member.contact ?? "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 회원 등록 모달 */}
      {currentCenter && (
        <CreateMemberModal
          isOpen={isCreateOpen}
          centerName={currentCenter.name}
          onClose={() => setIsCreateOpen(false)}
          onCreated={async () => {
            setIsCreateOpen(false);
            setSyncSuccess(false);
            setCreateSuccess(true);
            await fetchMembers();
          }}
        />
      )}
    </div>
  );
}
